/**
 * PatientInfoEditable — Inline display + edit for patient name, age, sex.
 *
 * Display mode:
 *   Rajesh Kumar  28  M        (name + age chip + gender chip)
 *   Rajesh Kumar  Age?  Sex?   (missing values → dashed placeholder chips)
 *
 * Edit interactions:
 *   - Age chip: tap → inline number/text input (supports "6m", "18d", etc.).
 *     OR-127: a usable DOB wins, so when the patient has one the chip shows the
 *     DOB-derived age read-only (the server would keep it anyway). The visit row
 *     carries no DOB, so the FIRST tap reads the patient once (cached per
 *     patient for this mount; one GET per user action at most, never on render).
 *   - Sex chip: tap → toggle buttons [M] [F] [O], single-tap saves
 *   - Name ✏️ icon: tap → first/last name inputs + ✓ save
 *   - Each field saves independently via PATCH /patients/{id}
 *
 * Props:
 *   visit       — visit object with patient_id, patient_name, patient_age, patient_gender, visit_id
 *   isDark      — dark mode flag
 *   editable    — allow editing (default true)
 *   compact     — smaller font for queue list rows (default false)
 *   onUpdate    — callback({ patient_name, patient_age, patient_gender }) after successful save
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { updatePatientDemographics, getPatient } from '../../../services/api/visitApi';
import { ageFromDob, patientAgeDisplay } from '../../../utils/ageValue';

// ── Helper: split "First Last" into parts ────────────────────────────
function splitName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/);
  if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

// ── Helper: gender display ───────────────────────────────────────────
function genderLabel(g) {
  if (!g) return null;
  const upper = g.toUpperCase();
  if (upper === 'MALE') return 'M';
  if (upper === 'FEMALE') return 'F';
  if (upper === 'OTHER') return 'O';
  return g.charAt(0).toUpperCase();
}

function genderFull(short) {
  if (short === 'M') return 'MALE';
  if (short === 'F') return 'FEMALE';
  if (short === 'O') return 'OTHER';
  return null;
}

// ── Styles ───────────────────────────────────────────────────────────
const chipBase = (isDark) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '0.0625rem 0.3125rem',
  borderRadius: '9999px',
  fontSize: '0.5625rem',
  fontWeight: 600,
  lineHeight: 1.4,
  cursor: 'pointer',
  transition: 'background 0.15s, border-color 0.15s',
  userSelect: 'none',
  whiteSpace: 'nowrap',
});

const chipFilled = (isDark) => ({
  ...chipBase(isDark),
  background: isDark ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.08)',
  color: isDark ? '#c4b5fd' : '#7c3aed',
  border: `1px solid ${isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.15)'}`,
});

const chipEmpty = (isDark) => ({
  ...chipBase(isDark),
  background: 'transparent',
  color: isDark ? '#6b7280' : '#9ca3af',
  border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
});

const toggleBtn = (isDark, active) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: '1.5rem', height: '1.375rem',
  borderRadius: '0.375rem',
  fontSize: '0.5625rem', fontWeight: 700,
  cursor: 'pointer',
  transition: 'all 0.12s ease',
  border: active
    ? `1.5px solid ${isDark ? '#a78bfa' : '#7c3aed'}`
    : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
  background: active
    ? (isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)')
    : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
  color: active
    ? (isDark ? '#c4b5fd' : '#7c3aed')
    : (isDark ? '#9ca3af' : '#6b7280'),
});

const miniInput = (isDark) => ({
  padding: '0.125rem 0.25rem',
  borderRadius: '0.375rem',
  border: `1px solid ${isDark ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.25)'}`,
  background: isDark ? 'rgba(0,0,0,0.3)' : '#fff',
  color: isDark ? '#e8e9ed' : '#111827',
  fontSize: '0.625rem',
  fontWeight: 500,
  outline: 'none',
  fontFamily: 'inherit',
});

export default function PatientInfoEditable({ visit, isDark = false, editable = true, compact = false, onUpdate, autoEdit = false }) {
  const patientName = visit?.patient_name || '';
  const patientAge = visit?.patient_age || '';
  const patientGender = visit?.patient_gender || '';
  const patientId = visit?.patient_id;
  const visitId = visit?.visit_id;

  // Edit states — each field independent
  const [editingAge, setEditingAge] = useState(false);
  const [editingGender, setEditingGender] = useState(false);
  const [editingName, setEditingName] = useState(false);

  const [ageValue, setAgeValue] = useState(patientAge);
  const [nameFirst, setNameFirst] = useState('');
  const [nameLast, setNameLast] = useState('');
  const [saving, setSaving] = useState(false);

  // DOB-derived age once known (OR-127): non-empty = age chip read-only.
  const [dobAge, setDobAge] = useState('');
  const dobCheckRef = useRef({ patientId: null, promise: null });
  const patientIdRef = useRef(patientId);
  patientIdRef.current = patientId;
  useEffect(() => { setDobAge(''); }, [patientId]); // same chip reused for the next patient (queue)
  const ageInputRef = useRef(null);
  const firstInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync from props when they change
  useEffect(() => { setAgeValue(patientAge); }, [patientAge]);

  // Auto-focus when editing opens
  useEffect(() => { if (editingAge && ageInputRef.current) ageInputRef.current.focus(); }, [editingAge]);
  useEffect(() => { if (editingName && firstInputRef.current) firstInputRef.current.focus(); }, [editingName]);

  // Auto-open edit mode for practice/tutorial flows
  useEffect(() => {
    if (autoEdit && editable && !patientName && !editingName) {
      // Small delay so the component mounts first
      const t = setTimeout(() => openNameEdit(), 300);
      return () => clearTimeout(t);
    }
  }, [autoEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save helper ──────────────────────────────────────────────────
  const saveField = useCallback(async (data) => {
    if (!patientId || saving) return;
    setSaving(true);
    try {
      await updatePatientDemographics(patientId, { ...data, visitId });
      // Compute what the visit fields should look like after the update
      const updates = {};
      if (data.firstName !== undefined || data.lastName !== undefined) {
        const fn = data.firstName !== undefined ? data.firstName : splitName(patientName).firstName;
        const ln = data.lastName !== undefined ? data.lastName : splitName(patientName).lastName;
        updates.patient_name = `${fn} ${ln}`.trim();
      }
      if (data.age !== undefined) updates.patient_age = data.age || '';
      if (data.gender !== undefined) updates.patient_gender = data.gender || '';
      onUpdate?.(updates);
    } catch (err) {
      console.error('Failed to update patient demographics:', err);
    } finally {
      setSaving(false);
    }
  }, [patientId, visitId, patientName, saving, onUpdate]);

  // ── Age save (debounced) ─────────────────────────────────────────
  const commitAge = useCallback(() => {
    const trimmed = ageValue.trim();
    if (trimmed !== patientAge) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveField({ age: trimmed });
      }, 300);
    }
    setEditingAge(false);
  }, [ageValue, patientAge, saveField]);

  // ── Age chip tap: open the editor only when the patient has no usable DOB ──
  const openAgeEdit = useCallback(() => {
    if (!editable || dobAge) return;
    const local = visit?.patient_dob || visit?.dob;
    if (local || !patientId) {
      if (ageFromDob(local)) setDobAge(patientAgeDisplay({ dob: local }));
      else setEditingAge(true);
      return;
    }
    const check = dobCheckRef.current;
    if (check.patientId !== patientId || !check.promise) {
      dobCheckRef.current = {
        patientId,
        promise: getPatient(patientId).then((p) => p?.dob || null).catch(() => null),
      };
    }
    dobCheckRef.current.promise.then((dob) => {
      if (patientIdRef.current !== patientId) return; // switched patient meanwhile
      if (ageFromDob(dob)) setDobAge(patientAgeDisplay({ dob }));
      else setEditingAge(true);
    });
  }, [editable, dobAge, visit, patientId]);

  // ── Gender save (immediate on tap) ───────────────────────────────
  const pickGender = useCallback((short) => {
    const full = genderFull(short);
    if (full !== patientGender) {
      saveField({ gender: full });
    }
    setEditingGender(false);
  }, [patientGender, saveField]);

  // ── Name save ────────────────────────────────────────────────────
  const commitName = useCallback(() => {
    const fn = nameFirst.trim();
    const ln = nameLast.trim();
    if (!fn) { setEditingName(false); return; }
    const { firstName: oldFirst, lastName: oldLast } = splitName(patientName);
    if (fn !== oldFirst || ln !== oldLast) {
      saveField({ firstName: fn, lastName: ln });
    }
    setEditingName(false);
  }, [nameFirst, nameLast, patientName, saveField]);

  const openNameEdit = useCallback(() => {
    if (!editable) return;
    const { firstName, lastName } = splitName(patientName);
    setNameFirst(firstName);
    setNameLast(lastName);
    setEditingName(true);
  }, [editable, patientName]);

  // ── Font sizes ───────────────────────────────────────────────────
  const nameFontSize = compact ? '0.75rem' : '0.875rem';
  const chipFontSize = compact ? '0.5rem' : '0.5625rem';
  const genderDisplay = genderLabel(patientGender);

  // ═════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap', minWidth: 0 }}>
      {/* ── Patient Name ──────────────────────────────────────── */}
      {editingName ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
          <input
            ref={firstInputRef}
            value={nameFirst}
            onChange={(e) => setNameFirst(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') setEditingName(false); }}
            placeholder="First"
            style={{ ...miniInput(isDark), width: '5rem' }}
          />
          <input
            value={nameLast}
            onChange={(e) => setNameLast(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') setEditingName(false); }}
            placeholder="Last"
            style={{ ...miniInput(isDark), width: '5rem' }}
          />
          <button
            onClick={commitName}
            disabled={saving || !nameFirst.trim()}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem',
              border: 'none', cursor: 'pointer',
              background: isDark ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.15)',
              color: '#10b981', fontSize: '0.625rem',
              opacity: saving ? 0.5 : 1,
            }}
          >
            ✓
          </button>
          <button
            onClick={() => setEditingName(false)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem',
              border: 'none', cursor: 'pointer',
              background: isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)',
              color: '#ef4444', fontSize: '0.625rem',
            }}
          >
            ✕
          </button>
        </span>
      ) : (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.1875rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontSize: nameFontSize, fontWeight: 600,
          color: isDark ? '#f5f5f7' : '#111827',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{patientName || 'Patient'}</span>
          {editable && (
            <span
              onClick={openNameEdit}
              style={{
                cursor: 'pointer', fontSize: '0.5rem', opacity: 0.4,
                transition: 'opacity 0.15s',
                marginLeft: '0.125rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; }}
              title="Edit name"
            >
              ✏️
            </span>
          )}
        </span>
      )}

      {/* ── Age Chip ──────────────────────────────────────────── */}
      {editingAge ? (
        <input
          ref={ageInputRef}
          value={ageValue}
          onChange={(e) => setAgeValue(e.target.value.slice(0, 4))}
          onBlur={commitAge}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } if (e.key === 'Escape') { setAgeValue(patientAge); setEditingAge(false); } }}
          placeholder="Age"
          style={{
            ...miniInput(isDark),
            width: '2.5rem', textAlign: 'center',
            fontSize: chipFontSize,
          }}
        />
      ) : (
        <span
          onClick={openAgeEdit}
          style={(dobAge || patientAge) ? { ...chipFilled(isDark), fontSize: chipFontSize, cursor: editable && !dobAge ? 'pointer' : 'default' } : { ...chipEmpty(isDark), fontSize: chipFontSize, cursor: editable ? 'pointer' : 'default' }}
          title={dobAge ? 'Calculated from date of birth' : (editable ? 'Click to edit age' : undefined)}
          data-testid="patient-info-age"
        >
          {dobAge || patientAge || (editable ? 'Age?' : '–')}
        </span>
      )}

      {/* ── Gender Chip / Toggle ──────────────────────────────── */}
      {editingGender ? (
        <span style={{ display: 'inline-flex', gap: '0.1875rem' }}>
          {['M', 'F', 'O'].map((g) => (
            <span
              key={g}
              onClick={() => pickGender(g)}
              style={{ ...toggleBtn(isDark, genderDisplay === g), fontSize: chipFontSize }}
            >
              {g}
            </span>
          ))}
        </span>
      ) : (
        <span
          onClick={() => editable && setEditingGender(true)}
          style={genderDisplay ? { ...chipFilled(isDark), fontSize: chipFontSize, cursor: editable ? 'pointer' : 'default' } : { ...chipEmpty(isDark), fontSize: chipFontSize, cursor: editable ? 'pointer' : 'default' }}
          title={editable ? 'Click to edit sex' : undefined}
        >
          {genderDisplay || (editable ? 'Sex?' : '–')}
        </span>
      )}

      {/* ── Saving indicator ──────────────────────────────────── */}
      {saving && (
        <span style={{ fontSize: '0.5rem', color: isDark ? '#6b7280' : '#9ca3af', fontStyle: 'italic' }}>
          saving…
        </span>
      )}
    </span>
  );
}
